import {
  Avatar,
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { useUser } from "../contexts/useUser";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchLLMAgents } from "../dataaccess/fetchLLMAgents";
import { useNavigate } from "react-router-dom";
import EmbedAgentComponent from "./EmbedAgentComponent";
import { createLLMAgentAfterEdit } from "../dataaccess/createLLMAgentAfterEdit";
import Calls from "./Calls";

const Agents: React.FC = () => {
  const { project } = useUser();
  const navigate = useNavigate();
  const [selectedAgentTab, setSelectedAgentTab] = React.useState(0);
  const [selectedAgent, setSelectedAgent] = React.useState<any>(null);
  const queryClient = useQueryClient();

  //embed menu
  const [menuState, setMenuState] = React.useState<{
    anchorEl: HTMLElement | null;
    agent: any | null;
  }>({
    anchorEl: null,
    agent: null,
  });

  const open = Boolean(menuState.anchorEl);

  useEffect(() => {
    queryClient.removeQueries({ queryKey: ["agents"] });
    setSelectedAgent(null);
  }, [project]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["agents"],
    queryFn: () => fetchLLMAgents(project?.id ?? ""),
    enabled: project?.id && project?.id.length > 0 ? true : false,
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    console.error("error", error);
    return <div>Error...</div>;
  }

  const agents = data?.data?.agentsByProjectId?.items ?? [];

  const getiFrameCode = (agent: any) => {
    return `<iframe
    src="${window.location.origin}/embedagent?id=${agent?.id}&customer_id=&customer_email="
    width="${agent?.displayWidth}"
    height="${agent?.displayHeight}"
    style="border: none;"
    allow="clipboard-read *; clipboard-write *"
  ></iframe>`;
  };

  const getReactCode = (agent: any) => {
    return `{/* setup:
1. npm i llmasaservice-ui 
2. add: import { AgentPanel } from "llmasaservice-ui";
3. if you haven't got a CSS file specified in the agent UI settings, you can use our default CSS file
import "../../node_modules/llmasaservice-ui/dist/index.css"; // check nesting level 
4. Add this code where you want the panel shown:  */}

<div> 
  <AgentPanel
    agent="${agent?.id}"
    customer={{
      // optional: omit the customer object if you don't want to use customer tracking features
      customer_id: "",
      customer_name: "",
      customer_user_email: "",
  }}
  />
</div>`;
  };

  const handleSelectedAgentTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setSelectedAgentTab(newValue);
  };

  return (
    <>
      <Box p={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              style={{ borderRadius: 3, border: "1px solid #444" }}
            >
              <Box height="100%">
                <List>
                  {agents.map((agent: any) => (
                    <ListItem
                      key={agent?.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        // Only select agent if we didn't click a button
                        if (!(e.target as HTMLElement).closest("button")) {
                          setSelectedAgent(agent);
                        }
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexGrow: 1,
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor:
                                agent?.status === "active"
                                  ? "primary.main"
                                  : "secondary.main",
                            }}
                          >
                            {agent?.name[0].toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>

                        <Box sx={{ marginLeft: 2 }}>
                          <ListItemText
                            primary={agent?.name}
                            secondary={`status: ${agent?.status}`}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          variant="text"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuState({
                              anchorEl: e.currentTarget,
                              agent: agent,
                            });
                          }}
                        >
                          Copy Embed Code
                        </Button>
                        <Menu
                          anchorEl={menuState.anchorEl}
                          open={open}
                          onClose={() =>
                            setMenuState({ anchorEl: null, agent: null })
                          }
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                        >
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(
                                getiFrameCode(menuState.agent)
                              );
                              setMenuState({ anchorEl: null, agent: null });
                            }}
                          >
                            iFrame Embed Code
                          </MenuItem>
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(
                                getReactCode(menuState.agent)
                              );
                              setMenuState({ anchorEl: null, agent: null });
                            }}
                          >
                            NPM Agent Panel (React, Next.js, etc)
                          </MenuItem>
                        </Menu>
                        <Button
                          variant="text"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`edit/${agent?.id}`);
                          }}
                        >
                          Edit
                        </Button>
                      </Box>
                    </ListItem>
                  ))}
                </List>
                <Button onClick={() => navigate("new")}>New Agent</Button>
                {agents?.length === 0 && (
                  <Box p={2}>
                    <Typography variant="body2">
                      No agents found, would you like an example?
                    </Typography>
                    <Button
                      onClick={async () => {
                        await createLLMAgentAfterEdit({
                          projectId: project?.id ?? "",
                          name: "Example Travel Things to do Agent",
                          status: "active",
                          description:
                            "This example shows how to create a travel itinerary agent.",
                          systemInstructions: `You are a travel expert and the user is going to give you a city. Your main role is to suggest detailed itineraries for cities worldwide, considering popular attractions, local culture, dining options, and any special activities. Stick strictly to the topic of travel, and provide thoughtful, practical, and engaging recommendations. When users ask questions, keep your responses relevant to travel, focusing on itinerary suggestions, tips for exploring destinations, and advice on how to make the most of their trips.
                    
If you recommend a restaurant or dining choice, format it EXACTLY: 
                      
yelp[display name::uri encoded name:::uri encoded location] 
                    
where display name is the restaurant name, and uri encoded name, and uri encoded location are search strings safe for url search params.
                    
At the end of the response add a [Search for hotels in the city they entered](https://www.google.com/travel/search?q=the city they entered)  for example, if they entered Seattle as the location city, use https://www.google.com/travel/search?q=seattle.
                    
If you're asked any off-topic questions, politely reply that you're focused on travel, and suggest some travel related questions they might ask.`,
                          systemInstructionsAction: "append",
                          groupId: "",

                          displayTheme: "dark",
                          cssUrl: "",
                          displayTitle: "Things to do in city agent",
                          displayStartMessageOrPrompt: "message",
                          displayInitialMessageOrPrompt:
                            "I can suggest ideas and itineraries for the city you specify. Add other details that help me design the perfect plan. For example, I'm visiting Seattle for 2 days and love the outdoors and art.e",
                          displayPromptTemplate: "{{prompt}}",
                          displayFollowOnPrompts:
                            "3 days in Seattle, I like hiking & food | 5 night Maui trip young family",
                          displayActions: `[
 {
    "pattern": "[.\\\\s]*yelp\\\\[(.*)::(.*)::(.*)\\\\].*",
    "type": "markdown",
    "markdown": " [$1](https://www.yelp.com/search?find_desc=$2&find_loc=$3) "
  }
]`,
                          displayWidth: "100%",
                          displayHeight: "75vh",
                          data: "",
                          displayPlaceholder:
                            "What city and how long? Or type a follow-on question",
                          displayHideInitialPrompt: false,
                          displayShowSaveButton: true,
                          displayShowEmailButton: true,
                          displayShowCallToAction: true,
                          displayCallToActionButtonText: "Contact us",
                          displayCallToActionEmailAddress:
                            "support@llmasaservice.io",
                          displayCallToActionEmailSubject:
                            "TTD Example Agent CTA submitted",
                        });

                        queryClient.invalidateQueries({ queryKey: ["agents"] });
                      }}
                    >
                      Create Example Agent
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              style={{ borderRadius: 3, border: "1px solid #444" }}
            >
              <Box p={2}>
                {!selectedAgent ? (
                  <Typography variant="body2">
                    Select an agent to view details
                  </Typography>
                ) : (
                  <Tabs
                    value={selectedAgentTab}
                    onChange={handleSelectedAgentTabChange}
                  >
                    <Tab label="Preview" />
                  </Tabs>
                )}

                {selectedAgent && selectedAgentTab === 0 && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "80vh",
                        p: 2,
                      }}
                    >
                      <iframe
                        src={`${window.location.protocol}//${window.location.host}/embedagent?id=${selectedAgent?.id}&customer_id=&customer_email=`}
                        width={selectedAgent?.displayWidth}
                        height={selectedAgent?.displayHeight}
                        style={{ flex: 1, border: "none" }}
                        allow="clipboard-read *; clipboard-write *"
                      ></iframe>
                    </Box>
                  </>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Agents;
